import { useRouter } from 'next/router';
import { HTMLAttributes, MouseEvent, useEffect, useMemo, useState } from 'react';
import Dialog from '@/dashboard/components/Dialog/index';
import CancelRenewalSuccessView from '@/dashboard/components/Products/policyCard/dialogs/cancelPolicy/cancelRenewalSuccess';
import CancelPolicyView from '@/dashboard/components/Products/policyCard/dialogs/cancelPolicy/index';
import RequestCancelPolicyDialog, {
  RequestCancelPolicyHandleTypeEnum,
} from '@/dashboard/components/Products/policyCard/dialogs/cancelPolicy/requestCancelPolicy';
import RequestCancelSuccessView from '@/dashboard/components/Products/policyCard/dialogs/cancelPolicy/requestCancelSuccess';
import ExtendPolicyView from '@/dashboard/components/Products/policyCard/dialogs/extendPolicy/index';
import PolicyDocumentView from '@/dashboard/components/Products/policyCard/dialogs/policyDocuments/index';
import { QuoteWidget } from '@/dashboard/components/QuoteWidget';
import { OPEN_EXTEND_CONSTRACT_POLICY_SAVE_KEY } from '@/dashboard/constants/storeKey';
import { useMaterialMedia } from '@/dashboard/hooks/useMaterialMedia';
import { useOpenGetQuote } from '@/dashboard/hooks/useOpenGetQuote';
import { useProjectNavigate } from '@/dashboard/hooks/useProjectNavigate';
import PolicyModel from '@/dashboard/models/policys/PolicyModel';
import { GridItem } from '@/dashboard/mui';
import { usePolicyDetailStore } from '@/dashboard/store/PolicyDetailStore';
import { usePolicyStore } from '@/dashboard/store/PolicyStore';
import { globalDataStore } from '@/dashboard/store/globalDataStore';
import { PolicyHandleSourceUpdate } from '@/dashboard/types/PolicyTypes';
import { getUpdatePolicyPath } from '@/dashboard/utils/routes';
import { DotsVerticalIcon } from '@heroicons/react/outline';
import { PencilIcon, ShieldExclamationIcon, XIcon } from '@heroicons/react/outline';
import { JoveProjectTypeEnum } from '@jove-frontend/enums';
import { JoveButton } from '@jove-frontend/mui';
import {
  LoadingMask,
  PROVIDER_SOURCE,
  PolicyState,
  PolicyStatus,
  REQUEST_SUCCESS_CODE,
  apiCancelRenewalPolicy,
  apiRevertPolicyToOriginalPolicyTerms,
} from '@jove-frontend/uikit';
import { Box, Button, Grid, Menu, MenuItem, Typography } from '@mui/material';
import clsx from 'clsx';
import { lintrk } from 'nextjs-linkedin-insight-tag';
import toast from 'react-hot-toast';
import { useAnalytics } from 'use-analytics';

type PolicyCardFooterProps = HTMLAttributes<HTMLDivElement> & {
  policy: PolicyModel;
  switchToAnnual: () => void;
};

const LinkedinConversionId = process.env.NX_LINKEDIN_CONVERSION_ID;
const ChubbClainUrlPath = process.env.NX_CHUBB_CLAIM_URL_PATH;

const PolicyCardFooter = (props: PolicyCardFooterProps) => {
  const router = useRouter();
  const { policy, switchToAnnual, className } = props;
  const [morePopover, setMorePopover] = useState(null);
  const [showExtendPolicyDialog, setShowExtendPolicyDialog] = useState(false);
  const [showCancelPolicyDialog, setShowCancelPolicyDialog] = useState(false);
  const [showPolicyDocumnetDialog, setShowPolicyDocumnetDialog] = useState(false);
  const { openGetQuoteIframe, setOpenGetQuoteIframe } = useOpenGetQuote();
  const { track } = useAnalytics();
  const [{ cleanAllPolicyDetailInfo }] = usePolicyDetailStore((store) => [store.actions]);
  const { isMobile } = useMaterialMedia();
  const {
    actions: { initialize },
  } = usePolicyStore();
  const {
    state: { queryParams },
  } = globalDataStore();
  const [menuLoading, setMenuLoading] = useState(false);
  const [dynamicHandleMap, setDynamicHandleMap] = useState({
    canRevertToTerms: false,
    canAcceptPolicy: false,
    canDisplayContainer: false,
  });
  const [loading, setLoading] = useState(false);
  const { push } = useProjectNavigate();
  const [showCancelRenewalSuccessDialog, setShowCancelRenewalSuccessDialog] = useState(false);
  const [showRequestCancelPolicySuccessDialog, setShowRequestCancelPolicySuccessDialog] = useState(false);
  const [requestCancelPolicyHandleType, setRequestCancelPolicyHandleType] = useState<RequestCancelPolicyHandleTypeEnum>(null);
  const [showRequestCancelPolicyDialog, setShowRequestCancelPolicyDialog] = useState(false);

  /**
   * request
   */
  const requestRevertPolicyToTerms = async () => {
    setLoading(true);
    const [res] = await apiRevertPolicyToOriginalPolicyTerms({
      policyId: policy?.id,
      apiSource: JoveProjectTypeEnum.dashboard,
    });
    setLoading(false);
    if (res?.code === REQUEST_SUCCESS_CODE) {
      toast.success('Revert successfully!');
      refreshHomePolicies();
    }
  };

  const requestCancelRenewalPolicy = async () => {
    const params = {
      policyId: policy?.id,
    };

    setMenuLoading(true);
    const [res] = await apiCancelRenewalPolicy(params);

    if (res?.code === REQUEST_SUCCESS_CODE) {
      setShowCancelRenewalSuccessDialog(true);
    }
    closeMorePopoverAction();
    setMenuLoading(false);
  };

  /**
   * effects
   */
  useEffect(() => {
    // notification click extend
    const needAlertPolicyId = sessionStorage.getItem(OPEN_EXTEND_CONSTRACT_POLICY_SAVE_KEY);
    if (needAlertPolicyId === policy.id) {
      sessionStorage.removeItem(OPEN_EXTEND_CONSTRACT_POLICY_SAVE_KEY);
      menuButtonClick('extendPolicy');
    }
  }, []);

  useEffect(() => {
    if (policy) {
      const canRevertToTerms =
        [PolicyStatus.POLICY_UPDATE_QUOTE, PolicyStatus.POLICY_UPDATE_REFERRAL].includes(policy.status) || (policy.haveDraft && policy.isRenewing);
      const canAcceptPolicy =
        [PolicyStatus.POLICY_UPDATE_QUOTE].includes(policy.status) ||
        (policy.haveDraft && policy.isRenewing && ![PolicyStatus.POLICY_RENEWED_UPDATE_REFERRAL].includes(policy.status));
      setDynamicHandleMap({
        canRevertToTerms,
        canAcceptPolicy,
        canDisplayContainer: canRevertToTerms || canAcceptPolicy,
      });
    }
  }, [policy]);

  useEffect(() => {
    const handleId = router.query.autoCancellationId;
    if (handleId) {
      // Auto display
      if (policy?.id === handleId && !sessionStorage.getItem(`cancel-${handleId}`)) {
        sessionStorage.setItem(`cancel-${handleId}`, 'true');
        setShowRequestCancelPolicyDialog(true);
      }
    }
  }, [router]);

  /**
   * methods
   */
  // show more popover view
  const showMoreAction = (event: MouseEvent<HTMLButtonElement>) => {
    setMorePopover(event.currentTarget);
  };
  const closeMorePopoverAction = () => {
    setMorePopover(null);
  };

  // show menu list
  const menuItemList = useMemo(() => {
    const list = [];
    if (policy.canExtendPolicy) {
      list.push({ value: 'extendPolicy', label: 'Extend policy' });
    }
    if (policy.isChubb) {
      if (policy.isActive || policy.isPaused) {
        list.push(
          ...[
            // { value: 'update', label: 'Update policy' },
            { value: 'cancel', label: 'Cancel policy' },
          ]
        );
      } else if (policy.isPending) {
        list.push({ value: 'cancel', label: 'Cancel policy' });
      }
    } else {
      // if (policy.canUpdate) {
      //   list.push({ value: 'update', label: 'Update policy' });
      // }
      if (policy.canCancel) {
        list.push({ value: 'cancel', label: 'Cancel policy' });
      }
      if (policy.canCancelRenewal) {
        list.push({ value: 'cancelRenewal', label: 'Cancel renewal' });
      }
      // if (policy.canRenew) {
      //   list.push({
      //     value: 'revertQuote',
      //     label: 'Revert to the latest accepted terms',
      //   });
      // }
    }

    return list;
  }, [policy]);

  // menu item action
  const menuButtonClick = (value) => {
    let closeMenu = true;
    switch (value) {
      case 'extendPolicy':
        setShowExtendPolicyDialog(true);
        break;
      case 'cancel':
        closeMenu = false;
        setShowRequestCancelPolicyDialog(true);
        break;
      case 'update':
        if (policy.isChubb) {
          cleanAllPolicyDetailInfo();
          push({ path: getUpdatePolicyPath(policy?.id) });
        } else {
          const params = {
            newDetail: true,
            detailSource: PolicyHandleSourceUpdate,
          };
          push({ path: getUpdatePolicyPath(policy?.id, params) });
        }
        break;
      case 'revertQuote':
        closeMenu = false;
        // requestRevertPolicyToOriginalPolicyTerms();
        break;
      case 'cancelRenewal':
        closeMenu = false;
        requestCancelRenewalPolicy();
        break;
      default:
        break;
    }
    if (closeMenu) {
      closeMorePopoverAction();
    }
  };

  // get a quote
  const getQuoteAction = () => {
    track('click_get_quote_button');
    lintrk('track', { conversion_id: LinkedinConversionId });
    setOpenGetQuoteIframe(true);
  };

  // make a claim
  const makeClaimAction = () => {
    window.open(ChubbClainUrlPath, '_blank');
  };

  const refreshHomePolicies = () => {
    initialize({ queryUserId: queryParams?.queryUserId });
  };

  const goToConfirmPolicyAction = () => {
    const params = {
      newDetail: true,
      detailSource: PolicyHandleSourceUpdate,
    };
    push({ path: getUpdatePolicyPath(policy?.id, params) });
  };

  const requestCancelPolicySuccessAction = () => {
    if (requestCancelPolicyHandleType === RequestCancelPolicyHandleTypeEnum.uploadFiles) {
      setShowRequestCancelPolicySuccessDialog(true);
    } else {
      refreshHomePolicies();
    }
  };

  const requestCancelPolicyHandleTypeChangeAction = (value) => {
    setRequestCancelPolicyHandleType(value);
  };

  return (
    <Box className={clsx('relative', className)}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <GridItem className="flex flex-col">
            {policy.isExpired && (
              <>
                <Typography className="font-bold" variant="subtitle2">
                  Expiry date:
                </Typography>
                <Typography className="font-medium" variant="subtitle2">
                  {policy.expiredDateText}
                </Typography>
              </>
            )}
            {policy.isCanceled && (
              <>
                <Typography className="font-bold" variant="subtitle2">
                  Cancellation date:
                </Typography>
                <Typography className="font-medium" variant="subtitle2">
                  {policy.canceledDateText}
                </Typography>
              </>
            )}
            {!policy.renewalQuotePayload && !(policy.isExpired || policy.isCanceled) && (
              <>
                <Typography className="font-bold" variant="subtitle2">
                  Policy period:
                </Typography>
                <Typography className="font-medium" variant="subtitle2">
                  {policy.policyStartDateText} - {policy.policyEndDateText}
                  {policy.dayLeft > 0 && <>（{policy.dayLeft} days left）</>}
                </Typography>
              </>
            )}
          </GridItem>
        </Grid>
        {policy.state !== PolicyState.POLICY_ELAPSED && (
          <Grid item xs={12} md={6}>
            <GridItem className="flex flex-row items-center justify-end">
              <Button
                className="rounded-full border-2 font-bold"
                size={isMobile ? 'small' : 'medium'}
                variant="outlined"
                onClick={() => setShowPolicyDocumnetDialog(true)}
              >
                Policy documents
              </Button>
              {policy.canMakeClaim && (
                <Button
                  className="ml-2 rounded-full border-2 font-bold"
                  size={isMobile ? 'small' : 'medium'}
                  variant="contained"
                  href={ChubbClainUrlPath}
                  target="_blank"
                >
                  Make a claim
                </Button>
              )}
              {policy.canGetNewQuote && (
                <Button
                  className="ml-2 rounded-full border-2 font-bold"
                  size={isMobile ? 'small' : 'medium'}
                  variant="contained"
                  onClick={getQuoteAction}
                >
                  Get new quote
                </Button>
              )}
              {menuItemList.length > 0 && (
                <>
                  <Button
                    id="show-more-policy-button"
                    style={{ minWidth: '24px' }}
                    className="ml-2 p-0"
                    variant="text"
                    aria-controls={open ? 'show-more-policy-button-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    onClick={showMoreAction}
                  >
                    <DotsVerticalIcon className="h-6 w-6" />
                  </Button>
                  <Menu
                    id="show-more-policy-button-menu"
                    anchorEl={morePopover}
                    open={!!morePopover}
                    onClose={closeMorePopoverAction}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                  >
                    <Box className="relative flex flex-col">
                      {menuLoading && <LoadingMask iconContainerClassName={menuItemList.length == 1 ? 'top-0' : ''} />}
                      {menuItemList.map((item) => {
                        return (
                          <MenuItem key={item.value} onClick={() => menuButtonClick(item.value)}>
                            {item.value === 'update' && <PencilIcon className="h-6 w-6" />}
                            {item.value === 'cancel' && <XIcon className="h-6 w-6" />}
                            {item.value === 'cancelRenewal' && <XIcon className="h-6 w-6" />}
                            {item.value === 'extendPolicy' && <ShieldExclamationIcon className="h-6 w-6" />}
                            <Typography className="ml-2" variant="body2">
                              {item.label}
                            </Typography>
                          </MenuItem>
                        );
                      })}
                    </Box>
                  </Menu>
                </>
              )}
            </GridItem>
          </Grid>
        )}
      </Grid>
      <Box className="mt-3 flex flex-row items-center justify-end space-x-2">
        {dynamicHandleMap.canRevertToTerms && <JoveButton onClick={requestRevertPolicyToTerms}>Revert to the latest accepted terms</JoveButton>}
        {dynamicHandleMap.canAcceptPolicy && (
          <JoveButton variant="contained" onClick={goToConfirmPolicyAction}>
            Accept quote and update
          </JoveButton>
        )}
      </Box>
      {showExtendPolicyDialog && (
        <Dialog close={() => setShowExtendPolicyDialog(false)} width="630px">
          <ExtendPolicyView className="mt-5" policy={policy} close={() => setShowExtendPolicyDialog(false)} switchToPolicy={switchToAnnual} />
        </Dialog>
      )}
      {showCancelPolicyDialog &&
        ([PROVIDER_SOURCE().CHUBB.value].includes(policy.providerCode) ? (
          <Dialog close={() => setShowCancelPolicyDialog(false)} width="600px">
            <CancelPolicyView className="mt-5" policy={policy} close={() => setShowCancelPolicyDialog(false)} />
          </Dialog>
        ) : null)}
      {showPolicyDocumnetDialog && (
        <Dialog close={() => setShowPolicyDocumnetDialog(false)} width="800px" showClose={false}>
          <PolicyDocumentView policy={policy} close={() => setShowPolicyDocumnetDialog(false)} />
        </Dialog>
      )}
      {openGetQuoteIframe && <QuoteWidget isOpen={openGetQuoteIframe} />}
      {showCancelRenewalSuccessDialog && (
        <Dialog showClose={false} width="600px">
          <CancelRenewalSuccessView info={policy} close={() => setShowCancelRenewalSuccessDialog(false)} refresh={refreshHomePolicies} />
        </Dialog>
      )}
      {showRequestCancelPolicySuccessDialog && (
        <Dialog close={() => setShowRequestCancelPolicySuccessDialog(false)} width="600px">
          <RequestCancelSuccessView close={() => setShowRequestCancelPolicySuccessDialog(false)} refresh={refreshHomePolicies} />
        </Dialog>
      )}
      {showRequestCancelPolicyDialog && (
        <Dialog close={() => setShowRequestCancelPolicyDialog(false)} width="600px">
          <RequestCancelPolicyDialog
            policy={policy}
            close={() => setShowRequestCancelPolicyDialog(false)}
            success={requestCancelPolicySuccessAction}
            handleTypeChange={requestCancelPolicyHandleTypeChangeAction}
          />
        </Dialog>
      )}
    </Box>
  );
};

export default PolicyCardFooter;
